// src/pages/GestionarCuentasVigilancia.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaSearch, FaTrash, FaCheckCircle, FaTimesCircle, FaEye, FaEyeSlash, FaDownload } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebase';
import { collection, query, where, onSnapshot, deleteDoc, doc, updateDoc, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toCsv, downloadCsv, normalizeRange } from '../utils/exportCsv';
import './GestionarCuentasVigilancia.css';

const GestionarCuentasVigilancia = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [casetaUsers, setCasetaUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const exportingLabelRef = React.useRef('');
  const { fraccionamientoId } = useAuth();

  useEffect(() => {
    if (!fraccionamientoId) {
      setCasetaUsers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    // Query usuarios importados desde Excel con rol='caseta' y createdByImport=true
    const usuariosRef = collection(db, 'usuarios');
    const q = query(
      usuariosRef,
      where('rol', '==', 'caseta'),
      where('fraccionamientoId', '==', fraccionamientoId),
      where('createdByImport', '==', true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const usuarios = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setCasetaUsers(usuarios);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error cargando cuentas de vigilancia:', err);
        setError('No se pudieron cargar las cuentas de vigilancia');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [fraccionamientoId]);

  const handleToggleStatus = useCallback(async (userId, currentStatus) => {
    try {
      const nuevoEstado = currentStatus ? 'inactivo' : 'activo';
      await updateDoc(doc(db, 'usuarios', userId), {
        estado: nuevoEstado
      });
    } catch (err) {
      console.error('Error actualizando estado:', err);
      setError('No se pudo actualizar el estado');
    }
  }, []);

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const filteredUsers = casetaUsers.filter(user =>
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const casetaDesignado = useMemo(() => {
    const activo = casetaUsers.find(u => u.estado === 'activo');
    return activo || casetaUsers[0] || null;
  }, [casetaUsers]);

  if (isLoading) {
    return (
      <div className="section-page gestion-vigilancia-page">
        <header className="section-header gestion-vigilancia-header">
          <h1 className="section-title">Vigilancia - Gestión de Cuentas</h1>
        </header>
        <div className="loading-message">Cargando cuentas de vigilancia...</div>
      </div>
    );
  }

  const runExport = async (producer, label) => {
    try {
      exportingLabelRef.current = label;
      const range = normalizeRange(exportFrom, exportTo);
      const { headers, rows, filename } = await producer(range);
      const csv = toCsv(rows, headers);
      const name = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
      downloadCsv(csv, name);
    } catch (e) {
      console.error('Error exportando', label, e);
      setError(`No se pudo exportar ${label}.`);
    } finally {
      exportingLabelRef.current = '';
    }
  };

  const handleExportVisitas = () => runExport((range) => fetchVisitasCsv(functions, fraccionamientoId, range), 'historial de visitas');
  const handleExportPaqueteria = () => runExport((range) => fetchPaqueteriaCsv(db, fraccionamientoId, range), 'paquetería');
  const handleExportFrecuentes = () => runExport((range) => fetchFrecuentesCsv(db, fraccionamientoId, range), 'visitas frecuentes');
  const handleExportProgramados = () => runExport(() => fetchProgramadosCsv(db, fraccionamientoId), 'programados');

  return (
    <div className="section-page gestion-vigilancia-page">
      <div className="vigilancia-container">
        <header className="gestion-vigilancia-header">
          <div>
            <h1 className="section-title"><FaUsers /> Vigilancia - Gestión de Cuentas</h1>
            <p className="section-subtitle">Administra las cuentas de los usuarios de caseta para vigilancia</p>
          </div>
          <button onClick={() => navigate('/administracion')} className="vigilancia-back-link">
            ← Regresar a administración
          </button>
        </header>
      </div>

      {error && (
        <div className="error-message-box">
          {error}
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}

      <div className="vigilancia-content">
        {/* Caseta designado actualmente */}
        <div className="designado-box">
          <div className="designado-title">Usuario de caseta designado actualmente</div>
          {casetaDesignado ? (
            <div className="designado-details">
              <div><strong>Nombre:</strong> {casetaDesignado.displayName || casetaDesignado.nombre || '—'}</div>
              <div><strong>Email:</strong> {casetaDesignado.email || '—'}</div>
              <div><strong>Estado:</strong> {casetaDesignado.estado === 'activo' ? 'Activo' : 'Inactivo'}</div>
            </div>
          ) : (
            <div className="designado-empty">No hay cuentas de caseta registradas.</div>
          )}
        </div>

        {/* Buscador de cuentas */}
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No hay cuentas de vigilancia registradas</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Estado</th>
                  <th>Fecha creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td className="cell-username">
                      {user.displayName || user.nombre || 'Sin nombre'}
                    </td>
                    <td>
                      {user.email || '—'}
                    </td>
                    <td>
                      <button
                        className={`status-badge ${user.estado === 'activo' ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleStatus(user.id, user.estado === 'activo')}
                        title={user.estado === 'activo' ? 'Clic para desactivar' : 'Clic para activar'}
                      >
                        {user.estado === 'activo' ? (
                          <>
                            <FaCheckCircle /> Activo
                          </>
                        ) : (
                          <>
                            <FaTimesCircle /> Inactivo
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      {user.createdAt?.toDate ? user.createdAt.toDate().toLocaleDateString('es-MX') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Exportaciones de vigilancia */}
        <div className="export-box">
          <div className="export-header">
            <h3><FaDownload /> Exportar concentrados de vigilancia</h3>
            <div className="export-range">
              <label>
                Desde
                <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} />
              </label>
              <label>
                Hasta
                <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} />
              </label>
            </div>
          </div>
          <div className="export-actions">
            <button className="btn-secondary" onClick={handleExportVisitas}>Historial de visitas</button>
            <button className="btn-secondary" onClick={handleExportPaqueteria}>Paquetería</button>
            <button className="btn-secondary" onClick={handleExportFrecuentes}>Visitas frecuentes</button>
            <button className="btn-secondary" onClick={handleExportProgramados}>Vehículos y proveedores programados</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Export handlers ---
async function fetchPaqueteriaCsv(db, fraccionamientoId, range) {
  const { from, to } = range;
  const rows = [];
  const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'paqueteria');
  const constraints = [];
  if (from) constraints.push(where('fechaRecepcion', '>=', Timestamp.fromDate(from)));
  if (to) constraints.push(where('fechaRecepcion', '<=', Timestamp.fromDate(to)));
  const q = constraints.length ? query(ref, ...constraints, orderBy('fechaRecepcion', 'desc')) : query(ref, orderBy('fechaRecepcion', 'desc'));
  const snap = await getDocs(q);
  snap.forEach((d) => {
    const data = d.data() || {};
    const fecha = data.fechaRecepcion?.toDate ? data.fechaRecepcion.toDate() : null;
    const entregado = data.entregadoEn?.toDate ? data.entregadoEn.toDate() : null;
    rows.push({
      id: d.id,
      departamento: data.departamento || '',
      empresa: data.empresa || '',
      destinatario: data.destinatario || '',
      guardia: data.guardia || '',
      fechaRecepcion: fecha ? fecha.toLocaleDateString('es-MX') : '',
      horaRecepcion: fecha ? fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
      estado: data.estado || '',
      fechaEntrega: entregado ? entregado.toLocaleDateString('es-MX') : '',
      horaEntrega: entregado ? entregado.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
      entregadoPor: (data.entregadoPor && typeof data.entregadoPor === 'object') ? data.entregadoPor.nombre : (data.entregadoPor || ''),
    });
  });
  const headers = [
    { key: 'id', label: 'ID' },
    { key: 'departamento', label: 'Departamento' },
    { key: 'empresa', label: 'Empresa' },
    { key: 'destinatario', label: 'Destinatario' },
    { key: 'guardia', label: 'Guardia' },
    { key: 'fechaRecepcion', label: 'Fecha recepción' },
    { key: 'horaRecepcion', label: 'Hora recepción' },
    { key: 'estado', label: 'Estado' },
    { key: 'fechaEntrega', label: 'Fecha entrega' },
    { key: 'horaEntrega', label: 'Hora entrega' },
    { key: 'entregadoPor', label: 'Entregado por' },
  ];
  return { headers, rows, filename: 'paqueteria' };
}

async function fetchFrecuentesCsv(db, fraccionamientoId, range) {
  const { from, to } = range;
  const rows = [];
  const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'visitasFrecuentes');
  const constraints = [];
  if (from) constraints.push(where('createdAt', '>=', Timestamp.fromDate(from)));
  if (to) constraints.push(where('createdAt', '<=', Timestamp.fromDate(to)));
  const q = constraints.length ? query(ref, ...constraints, orderBy('createdAt', 'desc')) : query(ref, orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  snap.forEach((d) => {
    const data = d.data() || {};
    const creado = data.createdAt?.toDate ? data.createdAt.toDate() : null;
    rows.push({
      id: d.id,
      nombre: data.nombre || '',
      categoria: data.categoria || '',
      departamento: data.ownerDepartamento || '',
      ownerNombre: data.ownerNombre || '',
      placa: data.placa || '',
      color: data.color || '',
      fechaRegistro: creado ? creado.toLocaleDateString('es-MX') : '',
      horaRegistro: creado ? creado.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
    });
  });
  const headers = [
    { key: 'id', label: 'ID' },
    { key: 'nombre', label: 'Nombre' },
    { key: 'categoria', label: 'Categoría' },
    { key: 'departamento', label: 'Departamento' },
    { key: 'ownerNombre', label: 'Registrado por' },
    { key: 'placa', label: 'Placa' },
    { key: 'color', label: 'Color' },
    { key: 'fechaRegistro', label: 'Fecha registro' },
    { key: 'horaRegistro', label: 'Hora registro' },
  ];
  return { headers, rows, filename: 'visitas_frecuentes' };
}

async function fetchProgramadosCsv(db, fraccionamientoId) {
  const rows = [];
  const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
  const q = query(ref, where('segmento', '==', 'solicitudes'));
  const snap = await getDocs(q);
  snap.forEach((d) => {
    const data = d.data() || {};
    const tipo = String(data.tipoSolicitud || '').toLowerCase();
    const estado = String(data.estado || '').toLowerCase();
    if (!['mudanza', 'construccion', 'remodelacion'].includes(tipo) || estado !== 'aprobado') return;
    const toDate = (v) => (v?.toDate ? v.toDate() : (v ? new Date(v) : null));
    const fechaProgramada = toDate(data.fechaProgramada);
    const fechaInicioObra = toDate(data.fechaInicioObra);
    const fechaInicioTrabajo = toDate(data.fechaInicioTrabajo);
    const fechaTerminoObra = toDate(data.fechaTerminoObra);
    rows.push({
      id: d.id,
      tipoSolicitud: tipo,
      residente: data.nombreCompleto || data.uploaderNombre || '',
      ubicacion: data.lote || data.houseLabel || data.numeroCasa || data.numeroLote || '',
      proveedor: data.empresaMudanza || data.empresa || data.empresaConstructora || data.encargadoObra || data.nombreContratista || data.responsableTrabajo || '',
      fechaProgramada: fechaProgramada ? fechaProgramada.toLocaleDateString('es-MX') : '',
      horaProgramada: fechaProgramada ? fechaProgramada.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
      fechaInicioObra: fechaInicioObra ? fechaInicioObra.toLocaleDateString('es-MX') : '',
      horaInicioObra: fechaInicioObra ? fechaInicioObra.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
      fechaInicioTrabajo: fechaInicioTrabajo ? fechaInicioTrabajo.toLocaleDateString('es-MX') : '',
      horaInicioTrabajo: fechaInicioTrabajo ? fechaInicioTrabajo.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
      fechaTerminoObra: fechaTerminoObra ? fechaTerminoObra.toLocaleDateString('es-MX') : '',
      horaTerminoObra: fechaTerminoObra ? fechaTerminoObra.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
      duracionDias: data.duracionDias || '',
    });
  });
  const headers = [
    { key: 'id', label: 'ID' },
    { key: 'tipoSolicitud', label: 'Tipo' },
    { key: 'residente', label: 'Residente' },
    { key: 'ubicacion', label: 'Ubicación' },
    { key: 'proveedor', label: 'Proveedor/Empresa' },
    { key: 'fechaProgramada', label: 'Fecha programada' },
    { key: 'horaProgramada', label: 'Hora programada' },
    { key: 'fechaInicioObra', label: 'Fecha inicio obra' },
    { key: 'horaInicioObra', label: 'Hora inicio obra' },
    { key: 'fechaInicioTrabajo', label: 'Fecha inicio trabajo' },
    { key: 'horaInicioTrabajo', label: 'Hora inicio trabajo' },
    { key: 'fechaTerminoObra', label: 'Fecha fin obra' },
    { key: 'horaTerminoObra', label: 'Hora fin obra' },
    { key: 'duracionDias', label: 'Duración (días)' },
  ];
  return { headers, rows, filename: 'programados' };
}

async function fetchVisitasCsv(functions, fraccionamientoId, range) {
  const { from, to } = range;
  const callable = httpsCallable(functions, 'listGuestVisitHistory');
  const resp = await callable({ fraccionamientoId, fromIso: from?.toISOString(), toIso: to?.toISOString(), limit: 2000 });
  const data = resp?.data || {};
  const items = Array.isArray(data.items) ? data.items : [];

  console.log('[fetchVisitasCsv] Datos recibidos:', {
    success: data.success,
    count: data.count,
    itemsLength: items.length,
    firstItem: items[0] || null,
  });

  // Este reporte debe mostrar TODAS las visitas (QR y manuales)
  const rows = items.map((item) => {
    // Parse entrada date - ensure proper ISO parsing
    let entradaDate = null;
    const entradaIso = item.entradaRegistradaIso || item.validadoEnIso || item.fechaVisitaIso;
    if (entradaIso) {
      try {
        // Ensure the ISO string is properly formatted
        const isoString = String(entradaIso);
        entradaDate = new Date(isoString);
        // Verify the date is valid
        if (isNaN(entradaDate.getTime())) {
          console.warn('[fetchVisitasCsv] Fecha de entrada inválida:', isoString);
          entradaDate = null;
        }
      } catch (e) {
        console.error('[fetchVisitasCsv] Error parsing entrada date:', e);
        entradaDate = null;
      }
    }

    // Parse salida date
    let salidaDate = null;
    if (item.salidaRegistradaIso) {
      try {
        salidaDate = new Date(item.salidaRegistradaIso);
        if (isNaN(salidaDate.getTime())) {
          salidaDate = null;
        }
      } catch (e) {
        console.error('[fetchVisitasCsv] Error parsing salida date:', e);
        salidaDate = null;
      }
    }

    // Determine estado label - convert to readable format
    let estadoLabel = item.estadoLabel || item.estado || '';
    if (!estadoLabel && item.estado) {
      // Convert estado values to readable labels
      const estadoMap = {
        'validado': 'VALIDADO',
        'salida_registrada': 'SALIDA REGISTRADA',
        'pendiente': 'PENDIENTE',
        'dentro': 'DENTRO',
        'fuera': 'FUERA',
      };
      estadoLabel = estadoMap[item.estado.toLowerCase()] || item.estado.toUpperCase();
    }

    // Extraer correos destino
    const correosDestino = Array.isArray(item.correosDestino)
      ? item.correosDestino.join(', ')
      : (item.correosDestino || '');

    // Determine acceso con QR (puede ser Sí o Manual)
    const accesoConQr = item.origen === 'qr'
      ? 'Sí'
      : (item.origen === 'manual'
        ? 'Manual'
        : (item.codigo
          ? 'Sí'
          : 'Manual'));

    return {
      id: item.id || '',
      visitante: item.visitante || 'Visitante sin nombre',
      departamento: item.numeroCasa || item.departamento || '',
      estado: estadoLabel,
      numeroVisitantes: item.numeroVisitantes || 1,
      accesoConQr: accesoConQr,
      codigoQr: item.codigo || '',
      categoria: item.categoria || '',
      placa: item.placa || '',
      color: item.color || '',
      notas: item.notas || '',
      correosDestino: correosDestino,
      entradaRegistradaPorNombre: item.entradaRegistradaPorNombre || item.validadoPorNombre || '',
      fechaEntrada: entradaDate && !isNaN(entradaDate.getTime()) ? entradaDate.toLocaleDateString('es-MX') : '',
      horaEntrada: entradaDate && !isNaN(entradaDate.getTime()) ? entradaDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
      capturasSeguridad: item.capturasSeguridad ? 'Sí' : 'No',
      salidaRegistradaPorNombre: item.salidaRegistradaPorNombre || '',
      fechaSalida: salidaDate && !isNaN(salidaDate.getTime()) ? salidaDate.toLocaleDateString('es-MX') : '',
      horaSalida: salidaDate && !isNaN(salidaDate.getTime()) ? salidaDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '',
    };
  });
  const headers = [
    { key: 'id', label: 'ID' },
    { key: 'visitante', label: 'Visitante' },
    { key: 'departamento', label: 'Departamento' },
    { key: 'estado', label: 'Estado' },
    { key: 'numeroVisitantes', label: 'Personas' },
    { key: 'accesoConQr', label: 'Acceso con QR' },
    { key: 'codigoQr', label: 'Código QR' },
    { key: 'categoria', label: 'Categoría' },
    { key: 'placa', label: 'Placa' },
    { key: 'color', label: 'Color' },
    { key: 'notas', label: 'Notas' },
    { key: 'correosDestino', label: 'Correos destino' },
    { key: 'entradaRegistradaPorNombre', label: 'Registró Entrada' },
    { key: 'fechaEntrada', label: 'Fecha entrada' },
    { key: 'horaEntrada', label: 'Hora entrada' },
    { key: 'capturasSeguridad', label: 'Capturas de Seguridad' },
    { key: 'salidaRegistradaPorNombre', label: 'Marcó salida' },
    { key: 'fechaSalida', label: 'Fecha salida' },
    { key: 'horaSalida', label: 'Hora salida' },
  ];
  console.log('📊 [fetchVisitasCsv] ========== RESULTADO FINAL ==========');
  console.log('📊 [fetchVisitasCsv] Total de filas en el CSV:', rows.length);
  console.log('📊 [fetchVisitasCsv] =========================================');

  return { headers, rows, filename: 'historial_visitas' };
}

export default GestionarCuentasVigilancia;

